import { EXPENSE_MESSAGES, isReimbursable, roundMoney } from "./expenseRules";
import {
  EXPENSE_PAID_BY_VALUES,
  type Expense,
  type ExpenseMonthlyTotal,
  type ExpensePaidBy,
} from "./expenseTypes";

/**
 * Almikia Epic EG (TASK-045): pure helpers of the monthly totals, the
 * reimbursement page and the CSV export of Gastos. Framework-free so they
 * unit-test without rendering anything.
 */

/** Spanish message when a reimbursement date is blank or in the future; undefined when valid. */
export function validateReimbursementDate(
  date: string | null | undefined,
  today: string,
): string | undefined {
  if (date == null || date.trim() === "") {
    return EXPENSE_MESSAGES.reimbursedAtRequired;
  }
  return date > today ? EXPENSE_MESSAGES.reimbursedAtFuture : undefined;
}

/** Uncancelled Pepe Diaz expenses split into pending and reimbursed (Sibaris / Otro never appear). */
export function splitReimbursements(expenses: Expense[]): {
  pending: Expense[];
  reimbursed: Expense[];
} {
  const eligible = expenses.filter(isReimbursable);
  return {
    pending: eligible.filter((expense) => !expense.reimbursed),
    reimbursed: eligible.filter((expense) => expense.reimbursed),
  };
}

/** Sum of the amounts of the given expenses, rounded to cents at every add. */
export function sumAmounts(expenses: Pick<Expense, "amount_mxn">[]): number {
  return expenses.reduce(
    (total, expense) => roundMoney(total + expense.amount_mxn),
    0,
  );
}

export type MonthlyBreakdownLine = {
  key: string;
  label: string;
  totalAmount: number;
  totalIva: number;
  count: number;
};

export type MonthSummary = {
  totalAmount: number;
  totalIva: number;
  count: number;
  /** One line per category, alphabetical (never ranked by amount). */
  byCategory: MonthlyBreakdownLine[];
  /** One line per payer in a fixed order (never ranked by amount). */
  byPaidBy: (MonthlyBreakdownLine & { paidBy: ExpensePaidBy })[];
};

/** `YYYY-MM` of a view `month` value (`YYYY-MM-DD`). */
export const monthKey = (month: string): string => month.slice(0, 7);

/** Distinct `YYYY-MM` months of the totals rows, newest first. */
export function listTotalsMonths(rows: ExpenseMonthlyTotal[]): string[] {
  return [...new Set(rows.map((row) => monthKey(row.month)))].sort().reverse();
}

const addRow = <Line extends MonthlyBreakdownLine>(
  line: Line,
  row: ExpenseMonthlyTotal,
): Line => ({
  ...line,
  totalAmount: roundMoney(line.totalAmount + row.total_amount_mxn),
  totalIva: roundMoney(line.totalIva + row.total_iva_mxn),
  count: line.count + row.expense_count,
});

const emptyLine = (key: string, label: string): MonthlyBreakdownLine => ({
  key,
  label,
  totalAmount: 0,
  totalIva: 0,
  count: 0,
});

/**
 * Totals of one `YYYY-MM` month from the `expense_monthly_totals` rows
 * (cancelled expenses are already excluded by the view). `payerLabel` names
 * each payer; the lines are never sorted by amount.
 */
export function summarizeMonth(
  rows: ExpenseMonthlyTotal[],
  month: string,
  payerLabel: (paidBy: ExpensePaidBy) => string,
): MonthSummary {
  const categories = new Map<string, MonthlyBreakdownLine>();
  const payers = new Map<
    ExpensePaidBy,
    MonthlyBreakdownLine & { paidBy: ExpensePaidBy }
  >(
    EXPENSE_PAID_BY_VALUES.map((paidBy) => [
      paidBy,
      { ...emptyLine(paidBy, payerLabel(paidBy)), paidBy },
    ]),
  );
  let total = emptyLine("total", "total");
  for (const row of rows) {
    if (monthKey(row.month) !== month) continue;
    total = addRow(total, row);
    const categoryKey = String(row.category_id);
    categories.set(
      categoryKey,
      addRow(
        categories.get(categoryKey) ??
          emptyLine(categoryKey, row.category_name),
        row,
      ),
    );
    const payer = payers.get(row.paid_by);
    if (payer) payers.set(row.paid_by, addRow(payer, row));
  }
  return {
    totalAmount: total.totalAmount,
    totalIva: total.totalIva,
    count: total.count,
    byCategory: [...categories.values()].sort((a, b) =>
      a.label.localeCompare(b.label, "es"),
    ),
    byPaidBy: [...payers.values()].filter((line) => line.count > 0),
  };
}

const money = (value: number | null | undefined): string =>
  value == null ? "" : value.toFixed(2);

/**
 * One CSV row of the Gastos export. Cancelled rows are kept, with an explicit
 * `cancelado` column and the reason so the file is auditable; money columns
 * always carry two decimals.
 */
export function toExpenseCsvRow(
  expense: Expense,
  payerLabel: (paidBy: ExpensePaidBy) => string,
): Record<string, string> {
  return {
    fecha: expense.expense_date,
    concepto: expense.concept,
    categoria: expense.category_name,
    proveedor: expense.supplier ?? "",
    cantidad_mxn: money(expense.amount_mxn),
    subtotal_mxn: money(expense.subtotal_mxn),
    iva_mxn: money(expense.has_iva ? expense.iva_amount : 0),
    facturado: expense.is_invoiced ? "si" : "no",
    uuid_cfdi: expense.cfdi_uuid ?? "",
    pagado_por:
      expense.paid_by === "otro" && expense.paid_by_other
        ? expense.paid_by_other
        : payerLabel(expense.paid_by),
    forma_de_pago: expense.payment_method,
    proyecto: expense.project_name ?? "",
    reembolsado: expense.reimbursed ? "si" : "no",
    fecha_reembolso: expense.reimbursed_at ?? "",
    cancelado: expense.cancelled_at ? "si" : "no",
    fecha_cancelacion: expense.cancelled_at?.slice(0, 10) ?? "",
    motivo_cancelacion: expense.cancel_reason ?? "",
    notas: expense.notes ?? "",
  };
}
